import React, { FC } from 'react';
import { useData } from '@hooks/useData';
import { OrderDetailManagementTable } from '../../OrderDetailManagementTable';
import { useTranslation } from 'react-i18next';
import { GetMyOrderDetailSuccessResponse } from '@services/useGetMyOrderDetail';
import { Typography } from '@components/atoms/Typography';

type Delivery = NonNullable<GetMyOrderDetailSuccessResponse['data']>['delivery'];

export const DeliveryTable: FC = () => {
    const { myOrderDetail, isLoadingMyOrderDetail } = useData();
    const { t } = useTranslation();

    const translations = {
        address: t('my-order-detail-delivery-table-component.header.address'),
        booking: t('my-order-detail-delivery-table-component.header.booking'),
        products: t('my-order-detail-delivery-table-component.header.products'),
        noBookings: t('my-order-detail-delivery-table-component.header.no-bookings'),
        digitalDelivery: t('my-order-detail-delivery-table-component.digital-delivery'),
    };

    const delivery = !isLoadingMyOrderDetail ? myOrderDetail?.data?.delivery || null : null;

    const deliveries = getDeliveries(delivery, translations.digitalDelivery);

    return (
        <>
            {delivery?.locker && delivery.locker.id ? (
                <>
                    <OrderDetailManagementTable
                        withActions={false}
                        header={[{ text: translations.address }]}
                        content={[
                            {
                                cells: [
                                    {
                                        content: (
                                            <>
                                                <Typography classes="mb-3" fontSize="16">
                                                    <>{delivery.locker?.name || ''}</>
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.address}
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.postalCode}
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.location}
                                                </Typography>
                                            </>
                                        ),
                                    },
                                ],
                                options: [],
                            },
                        ]}
                    />
                    <OrderDetailManagementTable
                        withActions={false}
                        header={[{ text: translations.booking }, { text: translations.products, hideOnMobile: true }]}
                        content={
                            deliveries.length > 0
                                ? deliveries.map((item) => {
                                      return {
                                          cells: [
                                              {
                                                  content: <b>{item.name || ''}</b>,
                                              },
                                              {
                                                  content: (
                                                      <>
                                                          {item.products?.map((product) => (
                                                              <p style={{ whiteSpace: 'pre-line' }}>{product}</p>
                                                          ))}
                                                      </>
                                                  ),
                                              },
                                          ],
                                          options: [],
                                      };
                                  })
                                : [
                                      {
                                          cells: [
                                              {
                                                  content: (
                                                      <Typography classes="mb-1" fontSize="14">
                                                          {translations.noBookings}
                                                      </Typography>
                                                  ),
                                              },
                                          ],
                                          options: [],
                                      },
                                  ]
                        }
                    />
                </>
            ) : (
                <OrderDetailManagementTable
                    withActions={false}
                    header={[{ text: translations.address }]}
                    content={deliveries.map((item) => {
                        return {
                            cells: [
                                {
                                    content: (
                                        <>
                                            {item.name ? (
                                                <>
                                                    <b>{item.name}</b>
                                                    <br />
                                                </>
                                            ) : null}
                                            {item.address && <p style={{ whiteSpace: 'pre-line' }}>{item.address}</p>}
                                        </>
                                    ),
                                },
                            ],
                            options: [],
                        };
                    })}
                />
            )}
        </>
    );
};

const getDeliveries: (
    delivery: Delivery | null,
    digitalDeliveryFallback: string
) => { id: string; name?: string; address?: string; products?: string[] }[] = (delivery, digitalDeliveryFallback) => {
    if (delivery?.shipToPickupSpot) {
        return [
            {
                id: '1',
                name: delivery.shipToPickupSpot.name,
                address: delivery.shipToPickupSpot.address || '',
            },
        ];
    }

    if (delivery?.pickupAtStore) {
        return delivery.pickupAtStore.map((item, index) => ({
            id: `${index}`,
            name: item.name,
            address: item.address || '',
        }));
    }

    const shippingAddress = delivery?.shipToAddress;
    if (delivery?.digitalDelivery) {
        const digitalLabel = typeof shippingAddress === 'string' ? shippingAddress : '';

        return [
            {
                id: '1',
                address: digitalLabel || digitalDeliveryFallback,
            },
        ];
    }

    if (delivery?.shipToAddress) {
        const shippingAddress = delivery?.shipToAddress;

        const shippingAddressText = shippingAddress
            ? `${shippingAddress?.recipient_first_name} ${shippingAddress?.recipient_last_name}` +
              `\nNIF: ${shippingAddress?.fiscal_number || '-'}` +
              `\n${shippingAddress.address_line1}` +
              `\n${shippingAddress.address_line2}` +
              `\n${shippingAddress.postal_code}` +
              `\n${shippingAddress.municipality}` +
              `\n${shippingAddress.region?.name}`
            : '';

        return [
            {
                id: '1',
                address: shippingAddressText,
            },
        ];
    }

    if (delivery?.locker) {
        return (delivery.lockerReservations || []).map((reservation, index) => ({
            id: `${index}`,
            name: reservation.store_name || '',
            products: reservation.orderItems?.map((item) => {
                return item.quantity + ' x ' + item.name + (item.attributes ? ', ' + item.attributes?.join(', ') : '');
            }),
        }));
    }

    return [];
};
